import { isDVMember } from "fieldpro-tools";
import _ from "lodash";

import {
  isClientBillingPlan,
  isClientBillingType,
} from "containers/authentication/utils/clientStatus";
import { ACTION_TYPE, OBJECT_CATEGORY } from "model/application/ActionCode";
import TLang from "model/application/Lang";
import { isOptimetriksAdminRole } from "model/constants/profiles";
import {
  CALENDAR_ROUTE,
  CLIENTS_ROUTE,
  CUSTOMERS_ROUTE,
  DASHBOARDS_ROUTE,
  DOCUMENTS_ROUTE,
  ENVIRONMENT_ROUTE,
  ERP_ROUTE,
  HOME_ROUTE,
  LISTS_ROUTE,
  PANORAMA_ROUTE,
  PICTURES_ROUTE,
  PLACES_ROUTE,
  TEAMS_ROUTE,
  USERS_ROUTE,
  WORKFLOWS_ROUTE,
} from "model/constants/routes";
import { BILLING_TYPE, IClient } from "model/entities/Client";
import { ISignedInUser } from "model/entities/User";

const { FETCH, CREATE } = ACTION_TYPE;
const {
  DASHBOARD,
  LIST,
  WORKFLOW,
  MOB_USER,
  WEB_USER,
  TEAM,
  DOCUMENT,
  CLIENT,
} = OBJECT_CATEGORY;

const getAction = (action: string, object: string) => `${action}_${object}`;

export type TRouteDefinition = {
  path: string;
  title: (lang: TLang) => string;
  icon: string;
  requiredActions?: string[];
  extraCondition?: (params: IExtraConditionParams) => boolean;
};

interface IExtraConditionParams {
  client: IClient;
  user?: ISignedInUser;
  lang: TLang;
}

export const ALL_ROUTES: TRouteDefinition[] = [
  {
    path: HOME_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.home,
    icon: "home",
  },
  {
    path: DASHBOARDS_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.dashboards,
    icon: "dashboard",
    requiredActions: [getAction(FETCH, DASHBOARD)],
  },
  {
    path: CUSTOMERS_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.customers,
    icon: "customers",
    requiredActions: [getAction(FETCH, LIST)],
  },
  {
    path: PLACES_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.map,
    icon: "place",
    requiredActions: [getAction(FETCH, LIST)],
  },
  {
    path: CALENDAR_ROUTE,
    title: (lang) => lang.containers.calendar.subCategories.calendar.subject,
    icon: "calendar",
    extraCondition: ({ client }) => !!client.enable_visit_planning,
  },
  {
    path: ERP_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.erp,
    icon: "erp",
    extraCondition: ({ client }) => !!client.is_fieldpro_connect_enabled,
  },
  {
    path: PANORAMA_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.panorama,
    icon: "panorama",
    extraCondition: ({ client }) =>
      !!client.is_fieldpro_connect_enabled &&
      client.erp_config?.type === "PANORAMA",
  },
  {
    path: WORKFLOWS_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.workflows,
    icon: "work",
    requiredActions: [getAction(FETCH, WORKFLOW)],
  },
  {
    path: LISTS_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.lists,
    icon: "list",
    requiredActions: [getAction(FETCH, LIST)],
  },
  {
    path: PICTURES_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.pictures,
    icon: "photo_camera",
    requiredActions: [getAction(FETCH, LIST), getAction(FETCH, DASHBOARD)],
  },
  {
    path: USERS_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.users,
    icon: "person",
    requiredActions: [getAction(FETCH, MOB_USER), getAction(FETCH, WEB_USER)],
  },
  {
    path: TEAMS_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.teams,
    icon: "group_work",
    requiredActions: [getAction(FETCH, TEAM)],
    extraCondition: ({ client }) =>
      !isClientBillingType(client, BILLING_TYPE.AUTOMATED) ||
      !isClientBillingPlan(client, "starter"),
  },
  {
    path: DOCUMENTS_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.documents,
    icon: "assignment",
    requiredActions: [getAction(FETCH, DOCUMENT)],
  },
  {
    path: ENVIRONMENT_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.environment,
    icon: "environment",
    requiredActions: [getAction(FETCH, DASHBOARD)],
    extraCondition: ({ client, user }) =>
      (isOptimetriksAdminRole(client?.profile) || isDVMember(user?.email)) &&
      !isClientBillingPlan(client, "starter"),
  },
  {
    path: CLIENTS_ROUTE,
    title: (lang) => lang.mainLayout.sidebar.clients,
    icon: "supervisor_account",
    requiredActions: [getAction(CREATE, CLIENT)],
  },
];

export const checkRequiredActions = (
  actionsAvailable: Record<string, boolean>,
  requiredActions: string[] | undefined,
) => {
  if (_.isEmpty(requiredActions)) {
    return true;
  }
  return _.every(requiredActions, (actionKey) => {
    return !!actionsAvailable[actionKey];
  });
};
